import { useCurrency } from '@/contexts/CurrencyContext'
import { useFiscalStatus } from '@/hooks/useFiscalStatus'
import { useAICopilotGate } from '@/hooks/useAICopilotGate'
import { openCopilot } from '@/components/ai/copilotBus'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ArrowDownRight,
  ArrowUpRight,
  Landmark,
  Minus,
  Receipt,
  ShoppingCart,
  Sparkles,
  TrendingDown,
  Users,
  Wallet,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { TARGET_LABEL, type ReportTarget } from '@/components/admin/reports/ReportsHeadline'
import type { DashboardOverview, IntMetricPair, MetricPair } from '@/types'
import {
  compactCurrency,
  formatCount,
  formatTrendPercent,
  metricPairOrEmpty,
  trendDirection,
  type TrendDirection,
} from './dashboardFormat'
import { cn } from '@/lib/utils'
import { MetricLabel } from '@/components/finance/MetricLabel'
import type { MetricKey } from '@/lib/financeGlossary'

// One row at desktop width for 4–6 tiles, so an optional tile (net profit
// after refunds, sales tax) never wraps onto a row of its own. Literal class
// strings so Tailwind keeps them.
const XL_COLS: Record<number, string> = {
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-3 xl:grid-cols-5',
  6: 'lg:grid-cols-3 xl:grid-cols-6',
}

interface KpiHeroRowProps {
  overview?: DashboardOverview
  isLoading: boolean
  previousLabel?: string
  /** F-FE-N205: opens the report behind a tile for the same window. */
  onOpen?: (target: ReportTarget) => void
}

// Headline metric is Sales — tax-inclusive takings after refunds, with the
// pre-refund figure folded underneath (F-RPT-N45). Net Sales lives in the
// P&L panel below.
export function KpiHeroRow({ overview, isLoading, previousLabel, onOpen }: KpiHeroRowProps) {
  const { formatCurrency, currencyCode } = useCurrency()
  const { enabled: fiscalEnabled } = useFiscalStatus()
  // F-AI-50: an "explain this" sparkle on each tile, only when the bubble is there.
  const askAI = useAICopilotGate().show

  if (isLoading || !overview) {
    return (
      <div className={cn('grid gap-4 sm:grid-cols-2', XL_COLS[fiscalEnabled ? 5 : 4])}>
        {Array.from({ length: fiscalEnabled ? 5 : 4 }).map((_, i) => (
          <KpiSkeleton key={i} />
        ))}
      </div>
    )
  }

  const refunds = metricPairOrEmpty(overview.refunds_issued)
  const grossAfterRefunds = metricPairOrEmpty(overview.gross_sales_after_refunds)
  const netSalesTax = metricPairOrEmpty(overview.net_sales_tax)
  const netProfitAfterRefunds = metricPairOrEmpty(overview.net_profit_after_refunds)
  // F-RPT-N1: surface the refund-adjusted Net Profit tile only when
  // refunds are non-zero in either window. The two numbers are equal
  // when there are no refunds, so showing both at all times is just
  // noise. The threshold matches the >Rs 0.01 floor we already use in
  // metricPairOrEmpty for "is this number meaningful?" decisions.
  const hasRefundActivity = refunds.current > 0.005 || refunds.previous > 0.005

  const tiles: KpiTileProps[] = [
    // F-RPT-N45: one card answers "how much did we take?". The headline is
    // sales after refunds; the pre-refund figure and the refunds that explain
    // the gap sit underneath, and only on a period that had refunds — a clean
    // day shows no "Rs 0 refunded" card.
    {
      label: 'Sales',
      target: 'daily',
      metric: 'gross_sales_after_refunds',
      icon: Wallet,
      iconClassName: 'text-emerald-600',
      value: compactCurrency(grossAfterRefunds.current, currencyCode),
      tooltip: formatCurrency(grossAfterRefunds.current),
      previous: formatCurrency(grossAfterRefunds.previous),
      pair: grossAfterRefunds,
      polarity: 'positive',
      breakdown:
        refunds.current > 0.005 ? (
          <span title={`${formatCurrency(overview.gross_sales.current)} sold − ${formatCurrency(refunds.current)} refunded`}>
            {compactCurrency(overview.gross_sales.current, currencyCode)} sold ·{' '}
            <span className="font-medium text-rose-600">{compactCurrency(refunds.current, currencyCode)} refunded</span>
          </span>
        ) : undefined,
    },
    {
      label: 'Orders',
      target: 'orders',
      metric: 'orders',
      icon: ShoppingCart,
      iconClassName: 'text-blue-600',
      value: formatCount(overview.orders.current),
      tooltip: `${formatCount(overview.orders.current)} completed (out of ${formatCount(
        overview.orders_placed.current,
      )} placed)`,
      previous: `${formatCount(overview.orders.previous)} prior`,
      pair: overview.orders,
      polarity: 'positive',
    },
    {
      label: 'Avg order value',
      target: 'items',
      metric: 'avg_ticket',
      icon: Receipt,
      iconClassName: 'text-violet-600',
      value: compactCurrency(overview.avg_ticket.current, currencyCode),
      tooltip: formatCurrency(overview.avg_ticket.current),
      previous: formatCurrency(overview.avg_ticket.previous),
      pair: overview.avg_ticket,
      polarity: 'positive',
    },
    {
      label: 'Guests',
      target: 'tables',
      metric: 'covers',
      icon: Users,
      iconClassName: 'text-amber-600',
      value: formatCount(overview.covers.current),
      tooltip: `${formatCount(overview.covers.current)} guests served`,
      previous: `${formatCount(overview.covers.previous)} prior`,
      pair: overview.covers,
      polarity: 'positive',
    },
  ]

  // F-RPT-N1: Net profit (after refunds) — appended conditionally so
  // it only takes a slot when the period actually has refunds. On a
  // clean day the two profit numbers are identical and showing both
  // is just visual noise. When refunds exist, this tile carries the
  // operator's *real* end-of-day economic position.
  if (hasRefundActivity) {
    tiles.push({
      label: 'Net profit (after refunds)',
      target: 'pnl',
      metric: 'net_profit_after_refunds',
      icon: TrendingDown,
      iconClassName: 'text-rose-600',
      value: compactCurrency(netProfitAfterRefunds.current, currencyCode),
      tooltip: `${formatCurrency(netProfitAfterRefunds.current)} — Net profit minus refunds in the same window`,
      previous: formatCurrency(netProfitAfterRefunds.previous),
      pair: netProfitAfterRefunds,
      polarity: 'positive',
      footnote: `Net profit ${formatCurrency(overview.net_profit.current)} − refunds ${formatCurrency(refunds.current)}`,
    })
  }

  // Sales tax tile — output VAT net of FBR credit notes, the figure to
  // compare against your PRA monthly portal filing. Only relevant for
  // tenants with a configured fiscal jurisdiction; non-fiscal tenants have
  // no PRA liability so the tile would always read zero.
  if (fiscalEnabled) {
    tiles.push({
      label: 'Sales tax (net)',
      target: 'tax',
      metric: 'net_sales_tax',
      icon: Landmark,
      iconClassName: 'text-indigo-600',
      value: compactCurrency(netSalesTax.current, currencyCode),
      tooltip: `${formatCurrency(netSalesTax.current)} — net of credit-note reversals`,
      previous: formatCurrency(netSalesTax.previous),
      pair: netSalesTax,
      polarity: 'positive',
    })
  }

  return (
    <div className={cn('grid gap-4 sm:grid-cols-2', XL_COLS[tiles.length] ?? 'lg:grid-cols-3 xl:grid-cols-6')}>
      {tiles.map((t) => (
        <KpiTile key={t.label} {...t} previousLabel={previousLabel} askAI={askAI} onOpen={onOpen} />
      ))}
    </div>
  )
}

interface KpiTileProps {
  label: string
  /** Glossary key drives the tooltip on the label itself. */
  metric: MetricKey
  icon: LucideIcon
  iconClassName?: string
  value: string
  /** Full-precision value shown on hover. */
  tooltip?: string
  previous: string
  pair: MetricPair | IntMetricPair
  polarity?: 'positive' | 'negative'
  previousLabel?: string
  /** Optional one-liner under the trend badge (e.g. net order count). */
  footnote?: string
  /** Optional rich one-liner under the trend badge (the Sales card's refund split). */
  breakdown?: ReactNode
  /** The report this tile opens (F-FE-N205). */
  target?: ReportTarget
  onOpen?: (target: ReportTarget) => void
  /** Show the "explain this" sparkle (the copilot bubble is mounted). */
  askAI?: boolean
}

/** The question the sparkle sends: the number, its change, and "why". */
export function explainMetricPrompt(label: string, value: string, previous: string, previousLabel?: string): string {
  const prior = previousLabel ? `${previous} (${previousLabel})` : previous
  return `${label} is ${value} now, it was ${prior} before — why? Give me the short reason.`
}

function KpiTile({ label, metric, icon: Icon, iconClassName, value, tooltip, previous, pair, polarity = 'positive', previousLabel, footnote, breakdown, target, onOpen, askAI }: KpiTileProps) {
  const dir = trendDirection(pair.pct, polarity)
  const pctText = formatTrendPercent(pair.pct)
  const open = target && onOpen ? () => onOpen(target) : undefined
  return (
    <Card
      role={open ? 'button' : undefined}
      tabIndex={open ? 0 : undefined}
      aria-label={open && target ? `${label}: ${value}. Open ${TARGET_LABEL[target]}` : undefined}
      onClick={open}
      onKeyDown={
        open
          ? e => {
              // Keys on the sparkle inside are the sparkle's, not the tile's.
              if (e.target !== e.currentTarget) return
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                open()
              }
            }
          : undefined
      }
      className={cn(
        'group',
        open &&
          'cursor-pointer transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex items-center justify-between">
          <MetricLabel
            metric={metric}
            label={label}
            className="text-sm font-medium text-muted-foreground"
          />
          <span className="flex items-center gap-1">
            {askAI && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation() // the tile itself opens a report
                  openCopilot({ seed: explainMetricPrompt(label, value, previous, previousLabel), send: true })
                }}
                aria-label={`Ask Bhookly AI about ${label}`}
                title="Ask Bhookly AI"
                className="rounded-md p-0.5 text-primary opacity-0 transition-opacity hover:bg-muted focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
              >
                <Sparkles className="h-4 w-4" aria-hidden />
              </button>
            )}
            {open && target ? (
              <span className="relative h-4 w-4">
                <Icon className={cn('absolute inset-0 h-4 w-4 transition-opacity group-hover:opacity-0', iconClassName)} />
                <ArrowUpRight
                  className="absolute inset-0 h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                  aria-hidden
                />
              </span>
            ) : (
              <Icon className={cn('h-4 w-4', iconClassName)} />
            )}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-3xl font-semibold leading-none tracking-tight" title={tooltip}>
            {value}
          </span>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <TrendBadge direction={dir} text={pctText ?? '—'} />
            <span className="truncate">
              vs {previous}
              {previousLabel ? ` (${previousLabel})` : ''}
            </span>
          </div>
          {footnote ? (
            <span className="text-[11px] text-muted-foreground">{footnote}</span>
          ) : null}
          {breakdown ? <span className="truncate text-[11px] text-muted-foreground">{breakdown}</span> : null}
        </div>
      </CardContent>
    </Card>
  )
}

const TREND_STYLES: Record<TrendDirection, { className: string; Icon: LucideIcon }> = {
  up: {
    className: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300',
    Icon: ArrowUpRight,
  },
  down: {
    className: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300',
    Icon: ArrowDownRight,
  },
  flat: {
    className: 'border-muted-foreground/20 bg-muted text-muted-foreground',
    Icon: Minus,
  },
  unknown: {
    className: 'border-muted-foreground/20 bg-muted text-muted-foreground',
    Icon: Minus,
  },
}

function TrendBadge({ direction, text }: { direction: TrendDirection; text: string }) {
  const { className, Icon } = TREND_STYLES[direction]
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] font-semibold', className)}>
      <Icon className="h-3 w-3" />
      {text}
    </span>
  )
}

function KpiSkeleton() {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-4 rounded" />
        </div>
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-3 w-32" />
      </CardContent>
    </Card>
  )
}
